import { useEffect, useState, type ReactNode } from 'react';
import { Gauge, Send, UserCheck } from 'lucide-react';
import type { ProcessingMode } from '../shared/desktop-api';
import {
  AUTOPILOT_CALIBRATION_NOTE,
  takeAutopilotCalibrationNote,
} from './autopilot-calibration-note';
import './autopilot.css';
import { hostHas } from '../shared/host-capabilities';

/**
 * Pilot mode: how much checking happens before a document leaves. Two modes since October 19, 2026
 * (owner decision): Manual and Autopilot. Autopilot is what Assisted was: a smarter AI checks what
 * Paige is unsure of, then the document goes out; on a computer that cannot run the AI reviewer
 * it goes out unchecked, as the old straight-through Autopilot sent it. The saved values are
 * ReviewPolicy.ProcessingMode: "human-reviewed" (Manual) and "ai-reviewed" (Autopilot); a job saved
 * as "straight-through" (the retired Autopilot) is shown and run as Autopilot.
 */
export const AUTOPILOT_MODES: ReadonlyArray<{ id: ProcessingMode; title: string; hover: string }> =
  [
    // One short line each on hover (tooltip audit, owner-approved October 5, 2026). Manual first (owner, 2026-10-03).
    { id: 'human-reviewed', title: 'Manual', hover: 'A person checks what it’s unsure of' },
    {
      id: 'ai-reviewed',
      title: 'Autopilot',
      hover: 'Everything goes out on its own; a smarter AI checks what it’s unsure of first',
    },
  ];

const MODE_ICONS: Record<'ai-reviewed' | 'human-reviewed', ReactNode> = {
  'human-reviewed': <UserCheck size={16} aria-hidden="true" />,
  'ai-reviewed': <Send size={16} aria-hidden="true" />,
};

/** What the chosen mode does with a document a check below catches, on this computer, said beside the choice. */
const MODE_SAYS: Record<ProcessingMode, ReactNode> = {
  'human-reviewed': (
    <>
      Anything below a bar <b>asks for review</b>. Everything else goes out on its own.
    </>
  ),
  'ai-reviewed': (
    <>
      Anything below a bar <b>is checked by Cortexa against the page</b>, then goes out. Cortexa
      reviews for you, and only stops if something isn’t working.
    </>
  ),
  'straight-through': (
    <>
      Every document <b>goes out as soon as it’s read</b>. The closer look needs a stronger graphics
      card than this computer has, so nothing below a bar gets one first.
    </>
  ),
};

/**
 * Pilot mode, the first of Review thresholds' two questions: when Paige is unsure (owner
 * decisions, 2026-10-03: chosen here, not in Export; one bar with a part per mode, none recommended
 * over another; the rest on hover).
 */
export function AutopilotChoices({
  mode,
  onMode,
  aiReviewAvailable,
  calibration,
}: {
  mode: ProcessingMode;
  /** Absent where the job cannot be edited; the chosen mode is then shown only. */
  onMode?: (mode: ProcessingMode) => void;
  /** Undefined until the check answers: the sentence then follows the chosen mode. */
  aiReviewAvailable: boolean | undefined;
  /**
   * The job, for the note a person sees once per job when they pick Autopilot while it is in Calibration (owner,
   * 2026-10-31; autopilot-calibration-note.ts). Sample jobs never see it.
   */
  calibration?: { jobId: string; calibrating: boolean; sample: boolean };
}) {
  const chosen = pilotMode(mode);
  // The job the note showed for in this view: it stays while Autopilot stays chosen, and never shows again for the job.
  const [notedFor, setNotedFor] = useState<string | null>(null);
  const pick = (next: ProcessingMode) => {
    if (next !== 'ai-reviewed') setNotedFor(null);
    else if (chosen !== 'ai-reviewed' && calibration && takeAutopilotCalibrationNote(calibration))
      setNotedFor(calibration.jobId);
    onMode?.(next);
  };
  const noteShown =
    chosen === 'ai-reviewed' && calibration !== undefined && notedFor === calibration.jobId;
  return (
    <section className="autopilot" aria-labelledby="autopilot-title">
      <div className="setup-part-head">
        <span className="setup-part-eyebrow">Pilot mode</span>
        <b id="autopilot-title">When Cortexa is unsure</b>
      </div>
      <div className="autopilot-row">
        <div className="autopilot-modes" role="radiogroup" aria-labelledby="autopilot-title">
          {AUTOPILOT_MODES.map((item) => {
            const selected = chosen === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={selected}
                title={item.hover}
                data-telemetry={item.id === 'ai-reviewed' ? 'pilot-autopilot' : 'pilot-manual'}
                disabled={!onMode}
                className={`autopilot-mode${selected ? ' selected' : ''}`}
                onClick={() => pick(item.id)}
              >
                {MODE_ICONS[item.id as 'ai-reviewed' | 'human-reviewed']}
                <span>{item.title}</span>
              </button>
            );
          })}
        </div>
        <p className="autopilot-says">
          {/* PAIGE: in Paige's cloud the reviewer always runs (the cell's reasoner), so the screen says what the
              chosen mode does and never the desktop's "this computer can't run the AI check" (Dustin, 2026-10-20). */}
          {
            MODE_SAYS[
              hostHas('local-performance')
                ? effectiveProcessingMode(mode, aiReviewAvailable)
                : pilotMode(mode)
            ]
          }
        </p>
      </div>
      {noteShown && (
        <p className="autopilot-calibration-note" role="status">
          <Gauge size={15} aria-hidden="true" />
          {AUTOPILOT_CALIBRATION_NOTE}
        </p>
      )}
    </section>
  );
}

/** The pilot mode a saved value means: Autopilot (the retired straight-through value included) or Manual (also when unset). */
export const pilotMode = (
  mode: ProcessingMode | undefined | null,
): 'ai-reviewed' | 'human-reviewed' =>
  mode === 'ai-reviewed' || mode === 'straight-through' ? 'ai-reviewed' : 'human-reviewed';

/**
 * What this computer actually does with a saved mode, as the engine decides it (AiReviewWorkstation.EffectiveMode):
 * "ai-reviewed" when Autopilot's AI check runs here, "straight-through" when Autopilot sends documents out
 * unchecked because this computer cannot run the AI reviewer, "human-reviewed" for Manual. Until the check
 * answers (undefined) it follows the chosen mode, so a computer that can run the check never flashes the
 * unchecked wording while the answer is on its way.
 */
export const effectiveProcessingMode = (
  mode: ProcessingMode | undefined | null,
  aiReviewAvailable: boolean | undefined,
): ProcessingMode =>
  pilotMode(mode) === 'human-reviewed'
    ? 'human-reviewed'
    : aiReviewAvailable === false
      ? 'straight-through'
      : 'ai-reviewed';

let aiReviewProbe: Promise<boolean> | null = null;

/**
 * Whether this computer can run the AI reviewer, from the engine's own hardware check
 * (WorkstationHealthCheck.AiReviewCapable). Fails closed: until the answer arrives, and
 * whenever it cannot be read, AI review is not offered.
 */
export function useAiReviewAvailable(): boolean {
  return useAiReviewAnswer() === true;
}

/** The same check, undefined until it answers (an unreadable answer is false). */
export function useAiReviewAnswer(): boolean | undefined {
  const [available, setAvailable] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let live = true;
    const probe = (aiReviewProbe ??= Promise.resolve(
      window.capturePointDesktop?.getWorkstationHealth?.(),
    )
      .then((health) => {
        if (!health) aiReviewProbe = null;
        return health?.aiReviewSupported === true;
      })
      .catch(() => {
        aiReviewProbe = null;
        return false;
      }));
    void probe.then((value) => {
      if (live) setAvailable(value);
    });
    return () => {
      live = false;
    };
  }, []);
  return available;
}
